/**
 * The message banner piece (web-account-pages D3): a message from the studio that arrives while
 * the messages panel is closed slides in under the site header for six seconds; a click opens the
 * panel at it. The app's client banner (in-app-notifications/client-notification-banner.html).
 */
import { definePiece } from '../../contract/define';

export const contract = definePiece({
  name: 'message-banner',
  description:
    "A banner under the site header for a message from the studio that arrives while the messages panel is closed: the studio's mark and name, the start of the message, now. Six seconds each, up to three held; a click opens the panel at that message. Only on a page whose header carries the client's messages (the account piece, or Telify's studio header): a custom site's page without it, such as a landing page, shows no banner.",
  options: {},
  parts: {
    host: { description: 'Where the banner shows, under the site header at the right.', required: false },
    banner: { description: 'The banner; a click opens the messages panel at its message.', required: false },
    logo: { description: "The studio's logo, when it has one.", required: false },
    title: { description: "The studio's name.", required: false },
    preview: { description: 'The start of the message.', required: false },
    time: { description: 'When it arrived: now.', required: false },
  },
  actions: {
    open: { description: "Opens the messages panel at the banner's message.", args: [] },
  },
  events: {
    opened: 'The client opened a message from its banner.',
  },
});

/** One banner. */
export interface MessageBannerItem {
  /** The message's id. */
  readonly id: string;
  /** The start of the message, in one line. */
  readonly preview: string;
}

/** What the message banner knows. */
export interface MessageBannerState {
  /** The banner showing; null when none is. */
  readonly banner: MessageBannerItem | null;
  /** True while the showing banner is on its way out. */
  readonly leaving: boolean;
  /** True while more banners wait behind this one. */
  readonly queued: boolean;
  /** The studio's name. */
  readonly studioName: string;
  /** The studio's logo image, or null. */
  readonly logoUrl: string | null;
}

/** What a drawing can ask the message banner to do. */
export interface MessageBannerActions {
  readonly open: () => void;
}

/** What the message banner tells its host. */
export interface MessageBannerEvents {
  readonly opened: { readonly id: string };
}
